import React, { useMemo, useRef } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import {
  DEFAULT_PRIMARY_COLOR,
  getReadableTextColor,
  hexToHsl,
  hslToHex,
  isValidHexColor,
  normalizeHexColor,
} from "../../utils/colorUtils";

const PRESET_PRIMARY_COLORS = [
  { value: "#244565", label: "Navy" },
  { value: "#84E1CC", label: "Mint" },
  { value: "#1E3A5F", label: "Deep blue" },
  { value: "#2D6A4F", label: "Forest" },
  { value: "#4361EE", label: "Royal blue" },
  { value: "#7B2CBF", label: "Purple" },
  { value: "#D62828", label: "Red" },
  { value: "#F77F00", label: "Orange" },
  { value: "#4E4E4E", label: "Charcoal" },
  { value: "#453939", label: "Brown" },
  { value: "#000000", label: "Black" },
  { value: "#FFFFFF", label: "White" },
];

const HUE_GRADIENT =
  "linear-gradient(to right, #FF0000 0%, #FFFF00 17%, #00FF00 33%, #00FFFF 50%, #0000FF 67%, #FF00FF 83%, #FF0000 100%)";

interface PrimaryColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  error?: string;
  onBlur?: () => void;
}

const PrimaryColorPicker: React.FC<PrimaryColorPickerProps> = ({
  value,
  onChange,
  error,
  onBlur,
}) => {
  const isMobile = useMediaQuery("(max-width:578px)");
  const colorInputRef = useRef<HTMLInputElement>(null);

  const selectedColor = isValidHexColor(value)
    ? normalizeHexColor(value)
    : DEFAULT_PRIMARY_COLOR;

  const [hue, saturation, lightness] = useMemo(
    () => hexToHsl(selectedColor),
    [selectedColor]
  );

  const handleColorSelect = (color: string) => {
    onChange(normalizeHexColor(color));
  };

  const handleHueChange = (nextHue: number) => {
    onChange(hslToHex(nextHue, saturation, lightness));
  };

  const handleCustomHexChange = (rawValue: string) => {
    const nextValue = rawValue.startsWith("#") ? rawValue : `#${rawValue}`;
    onChange(nextValue.slice(0, 7));
  };

  const handleCustomHexBlur = () => {
    if (isValidHexColor(value)) {
      onChange(normalizeHexColor(value));
    }
    onBlur?.();
  };

  const openNativePicker = () => {
    colorInputRef.current?.click();
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mb: 1,
        }}
      >
        <Typography className="input-label-custom" sx={{ mb: 0 }}>
          Primary brand colour <span style={{ color: "red" }}>*</span>
        </Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {selectedColor}
        </Typography>
      </Box>

      <Box
        sx={{
          border: "1px solid #E4E7EB",
          borderRadius: "12px",
          p: 1.25,
          backgroundColor: "#FAFBFC",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(34px, 1fr))",
            gap: 0.75,
            mb: 1.25,
          }}
        >
          {PRESET_PRIMARY_COLORS.map((color) => {
            const isSelected =
              normalizeHexColor(color.value) === selectedColor;
            const isLight =
              color.value.toUpperCase() === "#FFFFFF" ||
              color.value.toUpperCase() === "#84E1CC";

            return (
              <Tooltip key={color.value} title={color.label} arrow>
                <Box
                  role="button"
                  tabIndex={0}
                  aria-label={`Select ${color.label}`}
                  onClick={() => handleColorSelect(color.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleColorSelect(color.value);
                    }
                  }}
                  sx={{
                    width: "100%",
                    aspectRatio: "1",
                    borderRadius: "8px",
                    backgroundColor: color.value,
                    cursor: "pointer",
                    position: "relative",
                    border: isLight
                      ? "1px solid #D6D6D7"
                      : "1px solid transparent",
                    boxShadow: isSelected
                      ? "0 0 0 2px #84E1CC"
                      : "none",
                    "&:hover": { opacity: 0.9 },
                  }}
                >
                  {isSelected && (
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "7px",
                        backgroundColor: isLight
                          ? "rgba(0,0,0,0.06)"
                          : "rgba(0,0,0,0.15)",
                      }}
                    >
                      <CheckIcon
                        sx={{
                          color: isLight ? "#273848" : "#FFFFFF",
                          fontSize: 16,
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Tooltip>
            );
          })}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
            gap: 1,
            p: 1,
            borderRadius: "10px",
            backgroundColor: "#FFFFFF",
            border: "1px solid #ECEFF3",
            position: "relative",
          }}
        >
          <Tooltip title="Open colour palette" arrow>
            <Box
              onClick={openNativePicker}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  openNativePicker();
                }
              }}
              sx={{
                width: 44,
                height: 44,
                borderRadius: "10px",
                background: `conic-gradient(from 180deg, #FF6B6B, #FFD93D, #6BCB77, #4D96FF, #9B59B6, #FF6B6B)`,
                p: "2px",
                cursor: "pointer",
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "8px",
                  backgroundColor: selectedColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: getReadableTextColor(selectedColor),
                }}
              >
                <PaletteOutlinedIcon sx={{ fontSize: 18 }} />
              </Box>
            </Box>
          </Tooltip>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ position: "relative", height: 10 }}>
              <Box
                sx={{
                  height: 10,
                  borderRadius: 999,
                  background: HUE_GRADIENT,
                  border: "1px solid rgba(0,0,0,0.06)",
                }}
              />
              <Box
                component="input"
                type="range"
                min={0}
                max={360}
                value={hue}
                onChange={(event) =>
                  handleHueChange(Number(event.target.value))
                }
                aria-label="Hue slider"
                sx={{
                  position: "absolute",
                  inset: 0,
                  width: "100%",
                  height: 10,
                  opacity: 0,
                  cursor: "pointer",
                  m: 0,
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: `calc(${(hue / 360) * 100}% - 7px)`,
                  transform: "translateY(-50%)",
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  backgroundColor: hslToHex(hue, saturation, lightness),
                  border: "2px solid #FFFFFF",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
                  pointerEvents: "none",
                }}
              />
            </Box>
          </Box>

          <TextField
            size="small"
            placeholder="244565"
            value={value.replace(/^#/, "")}
            onChange={(event) => handleCustomHexChange(event.target.value)}
            onBlur={handleCustomHexBlur}
            error={!!error}
            sx={{
              width: isMobile ? "100%" : 118,
              flexShrink: 0,
              "& .MuiOutlinedInput-input": {
                padding: "7px 8px",
                fontFamily: "monospace",
                fontSize: "13px",
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0.5 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: "monospace" }}
                  >
                    #
                  </Typography>
                </InputAdornment>
              ),
            }}
          />

          {!isMobile && (
            <Tooltip title="Open colour palette" arrow>
              <IconButton
                size="small"
                onClick={openNativePicker}
                sx={{
                  border: "1px solid #E4E7EB",
                  borderRadius: "8px",
                  width: 36,
                  height: 36,
                  color: "#244565",
                }}
              >
                <PaletteOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}

          <input
            ref={colorInputRef}
            type="color"
            value={selectedColor}
            onChange={(event) => handleColorSelect(event.target.value)}
            aria-hidden
            tabIndex={-1}
            style={{
              position: "absolute",
              width: 0,
              height: 0,
              opacity: 0,
              pointerEvents: "none",
            }}
          />
        </Box>
      </Box>

      {error ? (
        <Typography
          variant="caption"
          color="error"
          sx={{ display: "block", mt: 0.5, pl: 0.5 }}
        >
          {error}
        </Typography>
      ) : null}
    </Box>
  );
};

export default PrimaryColorPicker;
